import React from 'react';
import { getAdminRoute, getAdminRoutes, setRouteActive } from '../../services/admin';
import { RouteCreator } from './RouteCreator';

const PAGE_SIZE = 50;

type RouteRecord = {
  id: string;
  code: string;
  name: string;
  origin: string;
  destination: string;
  stops: string;
  stopNames: string[];
  status: string;
  departure: string;
  arrival: string;
  driver: string;
  vehicle: string;
  fare: string;
};

export function RoutesPage({ searchQuery = '' }: { searchQuery?: string }) {
  const [routes, setRoutes] = React.useState<RouteRecord[]>([]);
  const [selectedRoute, setSelectedRoute] = React.useState<RouteRecord | null>(null);
  const [deactivatingRoute, setDeactivatingRoute] = React.useState<RouteRecord | null>(null);
  const [deactivationReason, setDeactivationReason] = React.useState('');
  const [deactivationNotes, setDeactivationNotes] = React.useState('');
  const [status, setStatus] = React.useState('');
  const [debouncedSearch, setDebouncedSearch] = React.useState(searchQuery);
  const [offset, setOffset] = React.useState(0);
  const [total, setTotal] = React.useState(0);
  const [isLoading, setIsLoading] = React.useState(true);
  const [error, setError] = React.useState('');
  const [message, setMessage] = React.useState('');
  const [activeRouteId, setActiveRouteId] = React.useState('');
  const [viewingRouteId, setViewingRouteId] = React.useState('');
  const [isCreating, setIsCreating] = React.useState(false);
  const [editingRoute, setEditingRoute] = React.useState<{ id: string; data: Record<string, unknown> } | null>(null);
  const [refreshKey, setRefreshKey] = React.useState(0);

  React.useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebouncedSearch(searchQuery.trim());
      setOffset(0);
    }, 300);
    return () => window.clearTimeout(timer);
  }, [searchQuery]);

  React.useEffect(() => {
    let isActive = true;
    setIsLoading(true);
    setError('');

    getAdminRoutes({ status, search: debouncedSearch, limit: PAGE_SIZE, offset })
      .then(async (response) => {
        if (!isActive) return;
        const records = normalizeRoutes(response);
        setRoutes(records);
        setTotal(readNestedNumber(response, ['total', 'count', 'routes_total', 'total_routes']) ?? records.length);

        const hydrated = await Promise.all(records.map(async (route) => {
          if (route.stopNames.length || !route.id) return route;
          try {
            const detail = normalizeRoutes(await getAdminRoute(route.id))[0];
            return detail ? { ...route, ...detail } : route;
          } catch {
            return route;
          }
        }));
        if (isActive) setRoutes(hydrated);
      })
      .catch((requestError) => {
        if (!isActive) return;
        setRoutes([]);
        setError(requestError instanceof Error ? requestError.message : 'Unable to load routes');
      })
      .finally(() => {
        if (isActive) setIsLoading(false);
      });

    return () => {
      isActive = false;
    };
  }, [debouncedSearch, offset, refreshKey, status]);

  React.useEffect(() => {
    if (!selectedRoute && !deactivatingRoute) return undefined;
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setSelectedRoute(null);
        setDeactivatingRoute(null);
      }
    };
    window.addEventListener('keydown', closeOnEscape);
    return () => window.removeEventListener('keydown', closeOnEscape);
  }, [deactivatingRoute, selectedRoute]);

  const pageStart = total ? offset + 1 : 0;
  const pageEnd = Math.min(offset + routes.length, total);

  const updateRouteStatus = async (
    route: RouteRecord,
    isActive: boolean,
    details?: { reason: string; notes: string },
  ) => {
    setActiveRouteId(route.id);
    setError('');
    setMessage('');
    try {
      await setRouteActive(route.id, isActive, details);
      const nextStatus = isActive ? 'active' : 'inactive';
      setRoutes((current) => current.map((item) => item.id === route.id ? { ...item, status: nextStatus } : item));
      setSelectedRoute((current) => current?.id === route.id ? { ...current, status: nextStatus } : current);
      setDeactivatingRoute(null);
      setDeactivationReason('');
      setDeactivationNotes('');
      setMessage(`${route.name} was ${isActive ? 'activated' : 'deactivated'}.`);
    } catch (requestError) {
      setError(requestError instanceof Error ? requestError.message : 'Unable to update route status');
    } finally {
      setActiveRouteId('');
    }
  };

  const viewRoute = async (route: RouteRecord) => {
    setViewingRouteId(route.id);
    setError('');
    try {
      const response = await getAdminRoute(route.id);
      setSelectedRoute(normalizeRoutes(response)[0] ?? route);
    } catch (requestError) {
      setError(requestError instanceof Error ? requestError.message : 'Unable to load route details');
    } finally {
      setViewingRouteId('');
    }
  };

  const editRoute = async (route: RouteRecord) => {
    setViewingRouteId(route.id);
    setError('');
    try {
      setEditingRoute({ id: route.id, data: await getAdminRoute(route.id) });
      setSelectedRoute(null);
    } catch (requestError) {
      setError(requestError instanceof Error ? requestError.message : 'Unable to load route for editing');
    } finally {
      setViewingRouteId('');
    }
  };

  return (
    <div className="routes-page">
      <div className="section-head drivers-head">
        <div>
          <h2>All routes</h2>
          <span>{total.toLocaleString()} route{total === 1 ? '' : 's'}</span>
        </div>
        <div className="driver-filters">
          <label>
            <span className="sr-only">Filter routes by status</span>
            <select value={status} onChange={(event) => { setStatus(event.target.value); setOffset(0); }}>
              <option value="">All statuses</option>
              <option value="scheduled">Scheduled</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
              <option value="completed">Completed</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </label>
          <button className="btn btn-primary" type="button" onClick={() => setIsCreating(true)}>Create route</button>
        </div>
      </div>

      {error ? <div className="kyc-message passengers-error" role="alert">{error}</div> : null}
      {message ? <div className="kyc-message" role="status">{message}</div> : null}

      <div className="data-table drivers-table routes-table">
        <table>
          <thead>
            <tr>
              <th>Code</th>
              <th>Name</th>
              <th>Origin</th>
              <th>Destination</th>
              <th>Stops</th>
              <th>Status</th>
              <th><span className="sr-only">Actions</span></th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr><td className="kyc-table-state" colSpan={7}>Loading routes...</td></tr>
            ) : routes.length ? routes.map((route) => (
              <tr className="route-table-row" key={route.id} tabIndex={0} aria-busy={viewingRouteId === route.id} onClick={() => viewRoute(route)} onKeyDown={(event) => { if (event.key === 'Enter') viewRoute(route); }}>
                <td data-label="Code"><span className="kyc-document">{route.code}</span></td>
                <td data-label="Name"><strong>{route.name}</strong></td>
                <td data-label="Origin">{route.origin}</td>
                <td data-label="Destination">{route.destination}</td>
                <td data-label="Stops">{route.stopNames.length}</td>
                <td data-label="Status"><span className={`pill ${statusTone(route.status)}`}>{route.status}</span></td>
                <td>
                  <div className="passenger-row-actions">
                    <button
                      aria-label={`View ${route.name}`}
                      className="btn-icon borderless passenger-view-btn"
                      disabled={viewingRouteId === route.id}
                      title="View route"
                      type="button"
                      onClick={(event) => { event.stopPropagation(); viewRoute(route); }}
                    >
                      <EyeIcon />
                    </button>
                    <button aria-label={`Edit ${route.name}`} className="btn-icon borderless passenger-view-btn" disabled={viewingRouteId === route.id} title="Edit route" type="button" onClick={(event) => { event.stopPropagation(); editRoute(route); }}>
                      <EditIcon />
                    </button>
                  </div>
                </td>
              </tr>
            )) : (
              <tr><td className="kyc-table-state" colSpan={7}>{debouncedSearch ? `No routes match "${debouncedSearch}".` : 'No routes found.'}</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="passengers-pagination">
        <span>Showing {pageStart}-{pageEnd} of {total.toLocaleString()}</span>
        <div>
          <button className="btn" disabled={offset === 0 || isLoading} type="button" onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}>Previous</button>
          <button className="btn" disabled={offset + PAGE_SIZE >= total || isLoading} type="button" onClick={() => setOffset(offset + PAGE_SIZE)}>Next</button>
        </div>
      </div>

      {selectedRoute ? (
        <div className="modal-backdrop" role="presentation" onMouseDown={() => setSelectedRoute(null)}>
          <section aria-labelledby="route-details-title" aria-modal="true" className="signup-dialog driver-details-dialog route-details-dialog" role="dialog" onMouseDown={(event) => event.stopPropagation()}>
            <div className="signup-dialog-head">
              <div>
                <span className="route-row-icon"><RouteIcon /></span>
                <div><h2 id="route-details-title">{selectedRoute.name}</h2><span className={`pill ${statusTone(selectedRoute.status)}`}>{selectedRoute.status}</span></div>
              </div>
              <button className="btn-icon borderless" type="button" aria-label="Close route details" onClick={() => setSelectedRoute(null)}><span aria-hidden="true">&times;</span></button>
            </div>
            <dl className="kyc-details">
              <RouteDetail label="Origin" value={selectedRoute.origin} />
              <RouteDetail label="Destination" value={selectedRoute.destination} />
              <RouteDetail label="Stops" value={selectedRoute.stops} />
              <RouteDetail label="Departure" value={selectedRoute.departure} />
              <RouteDetail label="Arrival" value={selectedRoute.arrival} />
              <RouteDetail label="Driver" value={selectedRoute.driver} />
              <RouteDetail label="Vehicle" value={selectedRoute.vehicle} document />
              <RouteDetail label="Fare" value={selectedRoute.fare} />
              <RouteDetail label="Route ID" value={selectedRoute.id} document />
            </dl>
            <div className="route-details-stops">
              <h3>Route stops</h3>
              {selectedRoute.stopNames.length ? (
                <ol>
                  {selectedRoute.stopNames.map((stop, index) => <li key={`${stop}-${index}`}><span>{index + 1}</span><strong>{stop}</strong></li>)}
                </ol>
              ) : <p>No stops were provided for this route.</p>}
            </div>
            <div className="kyc-dialog-actions"><button className="btn" type="button" onClick={() => setSelectedRoute(null)}>Close</button></div>
            <div className="kyc-dialog-actions">
              <button className="btn btn-outline" type="button" onClick={() => editRoute(selectedRoute)}>Edit route</button>
              {selectedRoute.status !== 'active' ? (
                <button className="btn btn-primary" disabled={activeRouteId === selectedRoute.id} type="button" onClick={() => updateRouteStatus(selectedRoute, true)}>Activate route</button>
              ) : (
                <button className="btn btn-danger" disabled={activeRouteId === selectedRoute.id} type="button" onClick={() => setDeactivatingRoute(selectedRoute)}>Deactivate route</button>
              )}
            </div>
          </section>
        </div>
      ) : null}

      {deactivatingRoute ? (
        <div className="modal-backdrop" role="presentation" onMouseDown={() => setDeactivatingRoute(null)}>
          <form
            aria-labelledby="deactivate-route-title"
            aria-modal="true"
            className="signup-dialog passenger-deactivate-dialog"
            role="dialog"
            onMouseDown={(event) => event.stopPropagation()}
            onSubmit={(event) => {
              event.preventDefault();
              updateRouteStatus(deactivatingRoute, false, {
                reason: deactivationReason.trim(),
                notes: deactivationNotes.trim(),
              });
            }}
          >
            <div className="signup-dialog-head">
              <div>
                <span className="route-row-icon"><RouteIcon /></span>
                <div><h2 id="deactivate-route-title">Deactivate route</h2><span>{deactivatingRoute.name}</span></div>
              </div>
              <button className="btn-icon borderless" type="button" aria-label="Close deactivation dialog" onClick={() => setDeactivatingRoute(null)}><span aria-hidden="true">&times;</span></button>
            </div>
            <div className="form-group">
              <label htmlFor="route-deactivation-reason">Reason</label>
              <input autoFocus id="route-deactivation-reason" placeholder="Reason for deactivation" required value={deactivationReason} onChange={(event) => setDeactivationReason(event.target.value)} />
            </div>
            <div className="form-group">
              <label htmlFor="route-deactivation-notes">Notes</label>
              <textarea id="route-deactivation-notes" placeholder="Additional notes (optional)" value={deactivationNotes} onChange={(event) => setDeactivationNotes(event.target.value)} />
            </div>
            <div className="kyc-dialog-actions">
              <button className="btn" type="button" onClick={() => setDeactivatingRoute(null)}>Cancel</button>
              <button className="btn btn-danger" disabled={!deactivationReason.trim() || activeRouteId === deactivatingRoute.id} type="submit">Deactivate route</button>
            </div>
          </form>
        </div>
      ) : null}

      {isCreating ? <RouteCreator onClose={() => setIsCreating(false)} onCreated={() => { setIsCreating(false); setOffset(0); setRefreshKey((current) => current + 1); setMessage('Route created successfully.'); }} /> : null}
      {editingRoute ? <RouteCreator routeId={editingRoute.id} initialData={editingRoute.data} onClose={() => setEditingRoute(null)} onCreated={() => { setEditingRoute(null); setRefreshKey((current) => current + 1); setMessage('Route updated successfully.'); }} /> : null}
    </div>
  );
}

function normalizeRoutes(response: unknown): RouteRecord[] {
  return getRouteSource(response).map((route, index) => {
    const driver = readObject(route, 'driver') ?? {};
    const vehicle = readObject(route, 'vehicle') ?? {};
    const origin = readLocation(route, 'origin_name', 'origin', 'start_location', 'pickup_location', 'from_location');
    const destination = readLocation(route, 'destination_name', 'destination', 'end_location', 'dropoff_location', 'to_location');
    return {
      id: readString(route, 'id', 'route_id') ?? String(index),
      code: readString(route, 'code', 'route_code', 'route_number') ?? 'Not provided',
      name: readString(route, 'name', 'route_name', 'title') ?? `${origin} to ${destination}`,
      origin,
      destination,
      stops: readStopNames(route).join(', ') || 'No stops',
      stopNames: readStopNames(route),
      status: (readString(route, 'status', 'route_status') ?? 'unknown').toLowerCase(),
      departure: formatDateTime(readString(route, 'departure_time', 'scheduled_departure', 'starts_at', 'start_time')),
      arrival: formatDateTime(readString(route, 'arrival_time', 'scheduled_arrival', 'ends_at', 'end_time')),
      driver: readString(route, 'driver_name') ?? readString(driver, 'full_name', 'name', 'username') ?? 'Not assigned',
      vehicle: readString(route, 'vehicle_registration_number', 'vehicle_plate_number', 'registration_number', 'plate_number') ?? readString(vehicle, 'registration_number', 'plate_number', 'name') ?? 'Not assigned',
      fare: formatFare(readValue(route, 'fare', 'price', 'amount')),
    };
  });
}

function getRouteSource(response: unknown): Record<string, unknown>[] {
  if (Array.isArray(response)) return response.filter(isObject).filter(isRouteObject);
  if (!isObject(response)) return [];
  if (isRouteObject(response)) return [response];

  for (const key of ['routes', 'route_list', 'items', 'results', 'records', 'data', 'payload', 'response']) {
    const value = response[key];
    if (Array.isArray(value)) {
      const routes = value.filter(isObject).filter(isRouteObject);
      if (routes.length || value.length === 0) return routes;
    }
    if (isObject(value)) {
      const routes = getRouteSource(value);
      if (routes.length) return routes;
    }
  }

  for (const [key, value] of Object.entries(response)) {
    if (key === 'stops' || key === 'route_stops') continue;
    const routes = getRouteSource(value);
    if (routes.length) return routes;
  }
  return [];
}

function isRouteObject(value: Record<string, unknown>) {
  if (!isObject(value)) return false;
  return [
    'route_code',
    'route_id',
    'origin_name',
    'destination_name',
    'origin',
    'destination',
  ].some((key) => key in value);
}

function readLocation(route: Record<string, unknown>, ...keys: string[]) {
  for (const key of keys) {
    const value = route[key];
    if (typeof value === 'string' && value) return value;
    if (isObject(value)) return readString(value, 'name', 'address', 'label', 'city') ?? 'Not provided';
  }
  return 'Not provided';
}

function RouteDetail({ document = false, label, value }: { document?: boolean; label: string; value: string }) {
  return <div><dt>{label}</dt><dd className={document ? 'kyc-document' : undefined}>{value}</dd></div>;
}

function RouteIcon() {
  return <svg width="22" height="22" viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><circle cx="6" cy="18" r="3" /><circle cx="18" cy="6" r="3" /><path d="M6 15V9a3 3 0 013-3h6" /></svg>;
}

function EyeIcon() {
  return <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="2"><path d="M2 12s3.5-6 10-6 10 6 10 6-3.5 6-10 6-10-6-10-6z" /><circle cx="12" cy="12" r="3" /></svg>;
}

function EditIcon() {
  return <svg width="19" height="19" viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 20h9" /><path d="M16.5 3.5a2.1 2.1 0 013 3L8 18l-4 1 1-4z" /></svg>;
}

function statusTone(status: string) {
  if (status === 'active' || status === 'completed') return 'success';
  if (status === 'scheduled') return 'info';
  if (status === 'cancelled') return 'danger';
  return 'muted';
}

function readStopNames(route: Record<string, unknown>): string[] {
  for (const key of ['stops', 'route_stops', 'stop_names']) {
    const value = route[key];
    if (Array.isArray(value)) {
      return value.map(readStopName).filter((name): name is string => Boolean(name));
    }
    if (isObject(value)) {
      const names = readStopNames(value);
      if (names.length) return names;
    }
  }
  for (const key of ['items', 'results', 'records', 'data']) {
    const value = route[key];
    if (Array.isArray(value)) {
      const names = value.map(readStopName).filter((name): name is string => Boolean(name));
      if (names.length) return names;
    }
  }
  return [];
}

function readStopName(stop: unknown) {
  if (typeof stop === 'string' || typeof stop === 'number') return String(stop);
  if (!isObject(stop)) return undefined;
  const location = readObject(stop, 'location') ?? readObject(stop, 'stop') ?? readObject(stop, 'point') ?? {};
  return readText(stop, 'name', 'stop_name', 'stopName', 'label', 'address')
    ?? readText(location, 'name', 'stop_name', 'stopName', 'label', 'address');
}

function formatDateTime(value: string | undefined) {
  if (!value) return 'Not provided';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
}

function formatFare(value: string | undefined) {
  if (!value) return 'Not provided';
  const amount = Number(value);
  return Number.isNaN(amount) ? value : new Intl.NumberFormat('en-NG', { style: 'currency', currency: 'NGN', maximumFractionDigits: 0 }).format(amount);
}

function readString(source: Record<string, unknown>, ...keys: string[]) {
  for (const key of keys) {
    const value = source[key];
    if (typeof value === 'string' && value) return value;
  }
  return undefined;
}

function readText(source: Record<string, unknown>, ...keys: string[]) {
  for (const key of keys) {
    const value = source[key];
    if (typeof value === 'string' || typeof value === 'number') return String(value);
  }
  return undefined;
}

function readValue(source: Record<string, unknown>, ...keys: string[]) {
  for (const key of keys) {
    const value = source[key];
    if (typeof value === 'string' || typeof value === 'number') return String(value);
  }
  return undefined;
}

function readObject(source: Record<string, unknown>, key: string) {
  return isObject(source[key]) ? source[key] as Record<string, unknown> : undefined;
}

function readNestedNumber(response: unknown, keys: string[]): number | undefined {
  if (!isObject(response)) return undefined;
  for (const key of keys) {
    if (typeof response[key] === 'number') return response[key] as number;
  }
  for (const value of Object.values(response)) {
    const nested = readNestedNumber(value, keys);
    if (nested !== undefined) return nested;
  }
  return undefined;
}

function isObject(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}
